// Subcontractors Declaration Form JavaScript Logic

// Enhanced Form Submission Manager for Subcontractors Declaration
class SubcontractorDeclarationFormSubmissionManager extends FormSubmissionManager {
    constructor() {
        super('subcontractorsDeclarationForm');
        this.initAntiSpam();
    }

    initAntiSpam() {
        // Set form load time for anti-spam checking
        const formLoadTimeField = document.getElementById('form_load_time');
        if (formLoadTimeField) {
            formLoadTimeField.value = new Date().toISOString();
        }
    }

    async handleSubmit(event) {
        event.preventDefault();
        
        // Prevent double submission
        if (this.isSubmitting) return;
        
        // Validate form before showing loading
        if (!this.validateSubcontractorForm()) {
            this.showWarning('Please complete all required fields and provide your digital signature before submitting.');
            return;
        }

        // Show loading with initial message
        this.showLoading('Processing Declaration', 'Validating your digital signature...');
        
        try {
            // Step 1: Collect and validate data
            this.updateProgress(20, 'Collecting form data...');
            const formData = this.collectFormData();
            
            if (!formData.signatureData) {
                throw new Error('Digital signature is required');
            }
            
            // Step 2: Prepare webhook data
            this.updateProgress(40, 'Preparing submission data...');
            const webhookData = this.prepareWebhookData(formData);
            
            // Step 3: Submit to webhook first
            this.updateProgress(60, 'Sending notifications...');
            try {
                await this.makeRequest('/Forms/SubcontractorDeclarationWebhookProxy', webhookData);
                this.updateProgress(75, 'Notifications sent successfully');
            } catch (webhookError) {
                console.warn('Webhook failed but continuing:', webhookError);
                this.updateProgress(75, 'Continuing with database save...');
            }
            
            // Step 4: Submit to database
            this.updateProgress(80, 'Saving to database...');
            const databaseResult = await this.makeRequest('/BackOffice/SubmitSubcontractorDeclaration', formData);
            
            if (!databaseResult.success) {
                throw new Error(databaseResult.message || 'Failed to save to database');
            }
            
            this.updateProgress(95, 'Database saved successfully');
            
            // Step 5: Success
            this.updateProgress(100, 'Declaration submitted!');
            
            // Reset form
            this.form.reset();
            clearSignature();
            updateFormSummary();
            
            // Show success and redirect
            this.showSuccess('Your Subcontractor Declaration has been submitted successfully!');
            
        } catch (error) {
            console.error('Subcontractor Declaration submission failed:', error);
            
            // Determine error type and show appropriate message
            if (error.message.includes('signature')) {
                this.showError(
                    'Your digital signature is required to complete the declaration. Please sign in the signature box and try again.',
                    true
                );
            } else if (error.message.includes('database') || error.message.includes('save')) {
                this.showError(
                    'There was a problem saving your declaration to our database. Please try again or contact support if the problem persists.',
                    true
                );
            } else if (error.message.includes('network') || error.message.includes('fetch')) {
                this.showError(
                    'There was a network connection problem. Please check your internet connection and try again.',
                    true
                );
            } else {
                this.showError(
                    'An unexpected error occurred while processing your declaration. Please try again or contact support if the problem persists.',
                    true
                );
            }
        }
    }

    validateSubcontractorForm() {
        const requiredFields = [
            { id: 'subcontractorBusinessName', name: 'Business Name' },
            { id: 'abn', name: 'ABN Number' },
            { id: 'fullAddress', name: 'Full Address' },
            { id: 'mobile', name: 'Mobile Number' },
            { id: 'clientBusinessName', name: 'Client Business Name' },
            { id: 'engagementDate', name: 'Engagement Date' },
            { id: 'servicesDescription', name: 'Services Description' },
            { id: 'declarantFirstName', name: 'First Name' },
            { id: 'declarantLastName', name: 'Last Name' },
            { id: 'declarantPosition', name: 'Position' }
        ];
        
        let isValid = true;
        let firstInvalidField = null;
        let errors = [];

        // Validate required fields
        requiredFields.forEach(field => {
            const element = document.getElementById(field.id);
            if (!element) return; // Skip if element doesn't exist
            
            const value = element.type === 'select-one' ? element.value : element.value.trim();
            
            if (!value) {
                element.style.borderColor = '#dc2626';
                element.style.backgroundColor = '#fef2f2';
                if (!firstInvalidField) firstInvalidField = element;
                errors.push(`${field.name} is required`);
                isValid = false;
            } else {
                element.style.borderColor = '#d1d5db';
                element.style.backgroundColor = '#ffffff';
            }
        });

        // Validate "other" position field if selected
        const positionElement = document.getElementById('declarantPosition');
        const otherPositionElement = document.getElementById('otherPosition');
        if (positionElement && positionElement.value === 'other') {
            if (!otherPositionElement || !otherPositionElement.value.trim()) {
                if (otherPositionElement) {
                    otherPositionElement.style.borderColor = '#dc2626';
                    otherPositionElement.style.backgroundColor = '#fef2f2';
                    if (!firstInvalidField) firstInvalidField = otherPositionElement;
                }
                errors.push('Please specify your position');
                isValid = false;
            } else if (otherPositionElement) {
                otherPositionElement.style.borderColor = '#d1d5db';
                otherPositionElement.style.backgroundColor = '#ffffff';
            }
        }

        // Validate ABN format (11 digits)
        const abnElement = document.getElementById('abn');
        if (abnElement) {
            const abn = abnElement.value.replace(/\s/g, ''); // Remove spaces
            if (abn && !abn.match(/^\d{11}$/)) {
                abnElement.style.borderColor = '#dc2626';
                errors.push('ABN must be 11 digits');
                isValid = false;
            }
        }

        // Validate Head Contractor ABN format (11 digits), only if provided
        const clientAbnElement = document.getElementById('clientAbn');
        if (clientAbnElement) {
            const clientAbn = clientAbnElement.value.replace(/\s/g, '');
            if (clientAbn && !clientAbn.match(/^\d{11}$/)) {
                clientAbnElement.style.borderColor = '#dc2626';
                errors.push('Head Contractor ABN must be 11 digits');
                isValid = false;
            }
        }

        // Validate mobile format
        const mobileElement = document.getElementById('mobile');
        if (mobileElement) {
            const mobile = mobileElement.value;
            if (mobile && !mobile.match(/^[\d\s\-\+\(\)]{10,15}$/)) {
                mobileElement.style.borderColor = '#dc2626';
                errors.push('Please enter a valid mobile number');
                isValid = false;
            }
        }

        // Validate date formats
        const engagementDateElement = document.getElementById('engagementDate');
        if (engagementDateElement && engagementDateElement.value && !validateDateFormat(engagementDateElement.value)) {
            engagementDateElement.style.borderColor = '#dc2626';
            engagementDateElement.style.backgroundColor = '#fef2f2';
            if (!firstInvalidField) firstInvalidField = engagementDateElement;
            errors.push('Please enter engagement date in DD/MM/YYYY format');
            isValid = false;
        }

        // Validate terms agreement
        const termsElement = document.getElementById('declarationConfirmation');
        const agreesToTerms = termsElement ? termsElement.checked : false;
        if (!agreesToTerms) {
            errors.push('You must agree to the terms and conditions');
            isValid = false;
        }

        // Validate required declaration checkboxes
        const requiredCheckboxes = [
            { id: 'confirmQuote', name: 'Confirmation: assessed the job / project and provided a quote' },
            { id: 'confirmOwnTools', name: 'Confirmation: use of own tools and supplies' },
            { id: 'confirmMaterialVariation', name: 'Confirmation: material variation entitlement' },
            { id: 'confirmPublicServices', name: 'Confirmation: services offered to the public' },
            { id: 'confirmEmployees', name: 'Confirmation: employs two (2) or more employees' },
            { id: 'confirmReturnToService', name: 'Confirmation: services may require return to site' },
            { id: 'confirmTaxResponsibility', name: 'Confirmation: responsible for all taxes including GST, payroll and income tax' },
            { id: 'confirmNoEmploymentRelationship', name: 'Confirmation: engagement does not create an employment relationship' }
        ];

        requiredCheckboxes.forEach(cb => {
            const el = document.getElementById(cb.id);
            if (!el || !el.checked) {
                errors.push(`${cb.name} is required`);
                isValid = false;
                if (el && !firstInvalidField) firstInvalidField = el;
            }
        });

        // Validate signature
        if (isSignatureEmpty()) {
            errors.push('Digital signature is required');
            isValid = false;
        }

        if (!isValid && firstInvalidField) {
            firstInvalidField.focus();
            firstInvalidField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        return isValid;
    }

    collectFormData() {
        const businessNameElement = document.getElementById('subcontractorBusinessName');
        const abnNumberElement = document.getElementById('abn');
        const clientAbnElement = document.getElementById('clientAbn');
        const fullAddressElement = document.getElementById('fullAddress');
        const mobileElement = document.getElementById('mobile');
        const clientBusinessNameElement = document.getElementById('clientBusinessName');
        const servicesElement = document.getElementById('servicesDescription');
        const firstNameElement = document.getElementById('declarantFirstName');
        const lastNameElement = document.getElementById('declarantLastName');
        const positionElement = document.getElementById('declarantPosition');
        const otherPositionElement = document.getElementById('otherPosition');
        const engagementDateElement = document.getElementById('engagementDate');
        const businessIdElement = document.getElementById('businessId');
        const emailElement = document.getElementById('email');
        const phoneElement = document.getElementById('phone');
        const contactPersonElement = document.getElementById('contactPerson');
        const workDescriptionElement = document.getElementById('workDescription');
        
        const signatureData = getSignatureDataURL();
        const signatureMetadata = getSignatureData();
        
        // Determine the actual position
        let actualPosition = positionElement ? positionElement.value : '';
        if (actualPosition === 'other' && otherPositionElement) {
            actualPosition = otherPositionElement.value;
        }
        
        const declarationText = generateDeclarationText();
        
        return {
            subcontractorBusinessName: businessNameElement ? businessNameElement.value : '',
            abn: abnNumberElement ? abnNumberElement.value : '',
            clientAbn: clientAbnElement ? clientAbnElement.value : '',
            fullAddress: fullAddressElement ? fullAddressElement.value : '',
            mobileNumber: mobileElement ? mobileElement.value : '',
            clientBusinessName: clientBusinessNameElement ? clientBusinessNameElement.value : '',
            engagementDate: engagementDateElement ? engagementDateElement.value : '',
            servicesDescription: servicesElement ? servicesElement.value : '',
            declarantFirstName: firstNameElement ? firstNameElement.value : '',
            declarantLastName: lastNameElement ? lastNameElement.value : '',
            declarantPosition: actualPosition,
            declarantFullName: `${firstNameElement ? firstNameElement.value : ''} ${lastNameElement ? lastNameElement.value : ''}`.trim(),
            declarationText: declarationText,
            businessId: businessIdElement ? businessIdElement.value : '',
            businessName: businessNameElement ? businessNameElement.value : '',
            contactPerson: contactPersonElement ? contactPersonElement.value : '',
            email: emailElement ? emailElement.value : '',
            phone: phoneElement ? phoneElement.value : '',
            workDescription: workDescriptionElement ? workDescriptionElement.value : '',
            agreesToTerms: true,
            confirmQuote: document.getElementById('confirmQuote')?.checked || false,
            confirmOwnTools: document.getElementById('confirmOwnTools')?.checked || false,
            confirmMaterialVariation: document.getElementById('confirmMaterialVariation')?.checked || false,
            confirmPublicServices: document.getElementById('confirmPublicServices')?.checked || false,
            confirmEmployees: document.getElementById('confirmEmployees')?.checked || false,
            confirmReturnToService: document.getElementById('confirmReturnToService')?.checked || false,
            confirmUnder90Days: document.getElementById('confirmUnder90Days')?.checked || false,
            confirmOver90Days: document.getElementById('confirmOver90Days')?.checked || false,
            confirmTaxResponsibility: document.getElementById('confirmTaxResponsibility')?.checked || false,
            confirmNoEmploymentRelationship: document.getElementById('confirmNoEmploymentRelationship')?.checked || false,
            signatureData: signatureData,
            signatureMetadata: JSON.stringify(signatureMetadata),
            signatureDate: new Date().toISOString()
        };
    }

    prepareWebhookData(formData) {
        return {
            form_type: 'Subcontractor Declaration',
            subcontractor_business_name: formData.subcontractorBusinessName,
            business_id: formData.businessId,
            abn_number: formData.abn,
            client_abn: formData.clientAbn,
            full_address: formData.fullAddress,
            mobile_number: formData.mobileNumber,
            client_business_name: formData.clientBusinessName,
            engagement_date: formData.engagementDate,
            services_description: formData.servicesDescription,
            declarant_first_name: formData.declarantFirstName,
            declarant_last_name: formData.declarantLastName,
            declarant_position: formData.declarantPosition,
            declarant_full_name: formData.declarantFullName,
            confirm_quote: formData.confirmQuote,
            confirm_own_tools: formData.confirmOwnTools,
            confirm_material_variation: formData.confirmMaterialVariation,
            confirm_public_services: formData.confirmPublicServices,
            confirm_employees: formData.confirmEmployees,
            confirm_return_to_service: formData.confirmReturnToService,
            confirm_under_90_days: formData.confirmUnder90Days,
            confirm_over_90_days: formData.confirmOver90Days,
            confirm_tax_responsibility: formData.confirmTaxResponsibility,
            confirm_no_employment_relationship: formData.confirmNoEmploymentRelationship,
            has_signature: formData.signatureData ? 'yes' : 'no',
            signature_provided: !!formData.signatureData,
            signature_image: formData.signatureData || null,
            signature_metadata: formData.signatureMetadata ? JSON.parse(formData.signatureMetadata) : null,
            signature_image_format: formData.signatureData ? 'image/png' : null,
            signature_timestamp: formData.signatureData ? new Date().toISOString() : null,
            declaration_text: formData.declarationText,
            submission_date: new Date().toISOString(),
            timestamp: Date.now()
        };
    }
}

let canvas, ctx, isDrawing = false;

function initializeSignature() {
    canvas = document.getElementById('signatureCanvas');
    
    // Set willReadFrequently attribute for performance optimization
    canvas.setAttribute('willReadFrequently', 'true');
    
    ctx = canvas.getContext('2d', { willReadFrequently: true });
    
    // Set canvas dimensions to match display size for proper coordinate mapping
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;
    
    // Set canvas properties
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    
    // Mouse events
    canvas.addEventListener('mousedown', startDrawing);
    canvas.addEventListener('mousemove', draw);
    canvas.addEventListener('mouseup', stopDrawing);
    canvas.addEventListener('mouseout', stopDrawing);
    
    // Touch events for mobile
    canvas.addEventListener('touchstart', handleTouch);
    canvas.addEventListener('touchmove', handleTouch);
    canvas.addEventListener('touchend', stopDrawing);
    
    // Handle window resize to maintain proper coordinate mapping
    window.addEventListener('resize', function() {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width;
        canvas.height = rect.height;
        ctx.putImageData(imageData, 0, 0);
        
        // Restore canvas properties after resize
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
    });
    
    console.log('Signature canvas initialized');
}

function startDrawing(e) {
    isDrawing = true;
    draw(e);
}

function draw(e) {
    if (!isDrawing) return;
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x, y);
    
    updateFormSummary();
}

function stopDrawing() {
    if (isDrawing) {
        isDrawing = false;
        ctx.beginPath();
    }
}

function handleTouch(e) {
    e.preventDefault();
    const touch = e.touches[0];
    
    // Create a more accurate mouse event with proper coordinates
    const rect = canvas.getBoundingClientRect();
    const mouseEvent = new MouseEvent(e.type === 'touchstart' ? 'mousedown' : 
                                    e.type === 'touchmove' ? 'mousemove' : 'mouseup', {
        clientX: touch.clientX,
        clientY: touch.clientY,
        bubbles: true
    });
    canvas.dispatchEvent(mouseEvent);
}

function clearSignature() {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    updateFormSummary();
    console.log('Signature cleared');
}

function isSignatureEmpty() {
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    return !imageData.data.some(channel => channel !== 0);
}

function getSignatureDataURL() {
    if (!canvas) return null;
    
    // Check if canvas is blank
    if (isSignatureEmpty()) {
        return null;
    }
    
    // Return high-quality PNG data URL
    return canvas.toDataURL('image/png', 1.0);
}

// Enhanced function to get signature data with metadata
function getSignatureData() {
    const dataURL = getSignatureDataURL();
    if (!dataURL) return null;
    
    return {
        image_data: dataURL,
        format: 'png',
        timestamp: new Date().toISOString(),
        canvas_width: canvas.width,
        canvas_height: canvas.height,
        file_size_estimate: Math.round(dataURL.length * 0.75) // Approximate file size in bytes
    };
}

// Date input formatting for DD/MM/YYYY
function formatDateInput(input) {
    let value = input.value.replace(/\D/g, ''); // Remove non-digits
    
    if (value.length >= 2) {
        value = value.substring(0, 2) + '/' + value.substring(2);
    }
    if (value.length >= 5) {
        value = value.substring(0, 5) + '/' + value.substring(5, 9);
    }
    
    input.value = value;
}

function validateDateFormat(dateString) {
    const regex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[012])\/([0-9]{4})$/;
    if (!regex.test(dateString)) return false;
    
    const [day, month, year] = dateString.split('/').map(Number);
    const date = new Date(year, month - 1, day);
    
    return date.getFullYear() === year && 
           date.getMonth() === month - 1 && 
           date.getDate() === day;
}

function convertToISODate(ddmmyyyy) {
    if (!ddmmyyyy || !validateDateFormat(ddmmyyyy)) return '';
    const [day, month, year] = ddmmyyyy.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
}

// Helper function to format dates in DD/MM/YYYY format
function formatAustralianDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
}

function updateFormSummary() {
    const summary = document.getElementById('formSummary');
    if (!summary) return; // Exit if summary element doesn't exist
    
    const businessNameElement = document.getElementById('subcontractorBusinessName');
    const abnNumberElement = document.getElementById('abn');
    const contactPersonElement = document.getElementById('declarantFirstName');
    const lastNameElement = document.getElementById('declarantLastName');
    const clientBusinessElement = document.getElementById('clientBusinessName');
    const engagementDateElement = document.getElementById('engagementDate');
    const declarationElement = document.getElementById('declarationConfirmation');

    const businessName = businessNameElement ? businessNameElement.value : '';
    const abnNumber = abnNumberElement ? abnNumberElement.value : '';
    const firstName = contactPersonElement ? contactPersonElement.value : '';
    const lastName = lastNameElement ? lastNameElement.value : '';
    const clientBusiness = clientBusinessElement ? clientBusinessElement.value : '';
    const engagementDate = engagementDateElement ? engagementDateElement.value : '';
    const agreesToTerms = declarationElement ? declarationElement.checked : false;
    const hasSignature = canvas && ctx ? !isSignatureEmpty() : false;

    const requiredCheckboxIds = [
        'confirmQuote',
        'confirmOwnTools',
        'confirmMaterialVariation',
        'confirmPublicServices',
        'confirmEmployees',
        'confirmReturnToService',
        'confirmTaxResponsibility',
        'confirmNoEmploymentRelationship'
    ];
    const allDeclarationsChecked = requiredCheckboxIds.every(id => {
        const el = document.getElementById(id);
        return el && el.checked;
    });

    let summaryText = '<div class="space-y-2">';

    if (businessName) {
        summaryText += `<div><strong>Business Name:</strong> ${businessName}</div>`;
    }

    if (abnNumber) {
        summaryText += `<div><strong>ABN:</strong> ${abnNumber}</div>`;
    }

    if (firstName || lastName) {
        summaryText += `<div><strong>Contact Person:</strong> ${firstName} ${lastName}</div>`;
    }

    if (clientBusiness) {
        summaryText += `<div><strong>Client:</strong> ${clientBusiness}</div>`;
    }

    if (engagementDate) {
        summaryText += `<div><strong>Engagement Date:</strong> ${formatAustralianDate(engagementDate)}</div>`;
    }

    summaryText += `<div><strong>Terms Accepted:</strong> ${agreesToTerms ? 'Yes' : 'No'}</div>`;
    summaryText += `<div><strong>Declarations Confirmed:</strong> ${allDeclarationsChecked ? 'Yes' : 'No'}</div>`;
    summaryText += `<div><strong>Digital Signature:</strong> ${hasSignature ? 'Provided' : 'Required'}</div>`;

    // Compliance status
    const isComplete = businessName && abnNumber && (firstName || lastName) &&
                      clientBusiness && engagementDate && agreesToTerms && allDeclarationsChecked && hasSignature;
    
    if (isComplete) {
        summaryText += '<div class="mt-4 p-3 bg-green-100 border border-green-400 rounded-lg">';
        summaryText += '<span class="text-green-800"><strong>✓ Declaration Complete</strong></span>';
        summaryText += '</div>';
    } else {
        summaryText += '<div class="mt-4 p-3 bg-yellow-100 border border-yellow-400 rounded-lg">';
        summaryText += '<span class="text-yellow-800"><strong>⚠ Declaration Incomplete</strong></span>';
        summaryText += '</div>';
    }
    
    summaryText += '</div>';
    
    if (summaryText === '<div class="space-y-2"></div>') {
        summaryText = '<p>Please complete the declaration form. A summary will appear here as you progress.</p>';
    }
    
    summary.innerHTML = summaryText;
}

function updateTermsAgreement() {
    const checkbox = document.getElementById('declarationConfirmation');
    const signatureSection = document.getElementById('signatureSection');
    const warningMessage = document.getElementById('termsWarning');
    
    if (checkbox && checkbox.checked) {
        if (signatureSection) {
            signatureSection.classList.remove('opacity-50', 'pointer-events-none');
        }
        if (warningMessage) {
            warningMessage.classList.add('hidden');
        }
    } else {
        if (signatureSection) {
            signatureSection.classList.add('opacity-50', 'pointer-events-none');
        }
        if (warningMessage) {
            warningMessage.classList.remove('hidden');
        }
        // Clear signature when terms are unchecked
        clearSignature();
    }
    
    updateFormSummary();
}

function updateDeclarationDisplay() {
    const firstNameElement = document.getElementById('declarantFirstName');
    const lastNameElement = document.getElementById('declarantLastName');
    const positionElement = document.getElementById('declarantPosition');
    const otherPositionElement = document.getElementById('otherPosition');
    const nameDisplayElement = document.getElementById('declarantNameDisplay');
    const positionDisplayElement = document.getElementById('declarantPositionDisplay');
    
    if (nameDisplayElement) {
        const firstName = firstNameElement ? firstNameElement.value : '';
        const lastName = lastNameElement ? lastNameElement.value : '';
        
        if (firstName || lastName) {
            nameDisplayElement.textContent = `${firstName} ${lastName}`.trim();
        } else {
            nameDisplayElement.textContent = '[First Name] [Last Name]';
        }
    }
    
    if (positionDisplayElement) {
        let position = '[Position]';
        
        if (positionElement && positionElement.value) {
            if (positionElement.value === 'other' && otherPositionElement && otherPositionElement.value) {
                position = otherPositionElement.value;
            } else if (positionElement.value !== 'other') {
                position = positionElement.options[positionElement.selectedIndex].text;
            }
        }
        
        positionDisplayElement.textContent = position;
    }
}

function toggleOtherPositionField() {
    const positionSelect = document.getElementById('declarantPosition');
    const otherPositionField = document.getElementById('otherPositionField');
    const otherPositionInput = document.getElementById('otherPosition');
    
    if (positionSelect && otherPositionField) {
        if (positionSelect.value === 'other') {
            otherPositionField.classList.remove('hidden');
            if (otherPositionInput) {
                otherPositionInput.required = true;
            }
        } else {
            otherPositionField.classList.add('hidden');
            if (otherPositionInput) {
                otherPositionInput.required = false;
                otherPositionInput.value = '';
            }
        }
    }
    
    updateDeclarationDisplay();
}

function formatABN(input) {
    // Remove all non-digit characters
    let value = input.value.replace(/\D/g, '');
    
    // Limit to 11 digits
    if (value.length > 11) {
        value = value.substring(0, 11);
    }
    
    // Format as XX XXX XXX XXX
    if (value.length > 2) {
        value = value.substring(0, 2) + ' ' + value.substring(2);
    }
    if (value.length > 6) {
        value = value.substring(0, 6) + ' ' + value.substring(6);
    }
    if (value.length > 10) {
        value = value.substring(0, 10) + ' ' + value.substring(10);
    }
    
    input.value = value;
    updateFormSummary();
}

function formatPhone(input) {
    // Basic phone formatting - remove non-digits and format
    let value = input.value.replace(/\D/g, '');
    
    // Australian mobile format
    if (value.startsWith('04') && value.length === 10) {
        value = value.substring(0, 4) + ' ' + value.substring(4, 7) + ' ' + value.substring(7);
    }
    // Australian landline format
    else if (value.length === 10 && (value.startsWith('02') || value.startsWith('03') || 
             value.startsWith('07') || value.startsWith('08'))) {
        value = value.substring(0, 2) + ' ' + value.substring(2, 6) + ' ' + value.substring(6);
    }
    
    input.value = value;
    updateFormSummary();
}

function generateDeclarationText() {
    const businessNameElement = document.getElementById('subcontractorBusinessName');
    const abnNumberElement = document.getElementById('abn');
    const firstNameElement = document.getElementById('declarantFirstName');
    const lastNameElement = document.getElementById('declarantLastName');
    const positionElement = document.getElementById('declarantPosition');
    const otherPositionElement = document.getElementById('otherPosition');
    
    const businessName = businessNameElement ? businessNameElement.value || '[Business Name]' : '[Business Name]';
    const abnNumber = abnNumberElement ? abnNumberElement.value || '[ABN Number]' : '[ABN Number]';
    const firstName = firstNameElement ? firstNameElement.value || '[First Name]' : '[First Name]';
    const lastName = lastNameElement ? lastNameElement.value || '[Last Name]' : '[Last Name]';
    let position = '[Position]';
    
    if (positionElement && positionElement.value) {
        if (positionElement.value === 'other' && otherPositionElement && otherPositionElement.value) {
            position = otherPositionElement.value;
        } else if (positionElement.value !== 'other') {
            position = positionElement.options[positionElement.selectedIndex].text;
        }
    }
    
    return `I, ${firstName} ${lastName}, a ${position} of the subcontractor making this declaration, hereby declare that I am in a position to know the truth of the matters which are contained in this document and declare to the best of my knowledge to be true and accurate.

Business Details:
- Business Name: ${businessName}
- ABN: ${abnNumber}

This declaration is made on ${formatAustralianDate(new Date().toISOString().split('T')[0])} and constitutes a legal commitment under Australian law.`;
}

// Initialize form with enhanced submission handling
let formSubmissionManager;

document.addEventListener('DOMContentLoaded', function() {
    console.log('Initializing Subcontractors Declaration form...');
    
    // Initialize signature canvas
    initializeSignature();
    
    // Initialize enhanced form submission manager
    formSubmissionManager = new SubcontractorDeclarationFormSubmissionManager();
    
    // Add event listeners for form summary updates
    const formFields = document.querySelectorAll('input[type="text"], input[type="email"], input[type="tel"], textarea');
    formFields.forEach(field => {
        field.addEventListener('input', updateFormSummary);
        field.addEventListener('change', updateFormSummary);
    });

    // Terms checkbox
    const termsCheckbox = document.getElementById('declarationConfirmation');
    if (termsCheckbox) {
        termsCheckbox.addEventListener('change', updateTermsAgreement);
    }

    // Declaration confirmation checkboxes — update summary on change
    [
        'confirmQuote', 'confirmOwnTools', 'confirmMaterialVariation',
        'confirmPublicServices', 'confirmEmployees', 'confirmReturnToService',
        'confirmUnder90Days', 'confirmOver90Days',
        'confirmTaxResponsibility', 'confirmNoEmploymentRelationship'
    ].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.addEventListener('change', updateFormSummary);
    });

    // Declaration fields for real-time display update
    const firstNameInput = document.getElementById('declarantFirstName');
    if (firstNameInput) {
        firstNameInput.addEventListener('input', updateDeclarationDisplay);
    }
    
    const lastNameInput = document.getElementById('declarantLastName');
    if (lastNameInput) {
        lastNameInput.addEventListener('input', updateDeclarationDisplay);
    }
    
    const positionSelect = document.getElementById('declarantPosition');
    if (positionSelect) {
        positionSelect.addEventListener('change', function() {
            toggleOtherPositionField();
            updateDeclarationDisplay();
        });
    }
    
    const otherPositionInput = document.getElementById('otherPosition');
    if (otherPositionInput) {
        otherPositionInput.addEventListener('input', updateDeclarationDisplay);
    }

    // Add date formatting for engagement and signature dates
    const engagementDateInput = document.getElementById('engagementDate');
    if (engagementDateInput) {
        engagementDateInput.addEventListener('input', function() {
            formatDateInput(this);
            updateFormSummary();
        });
    }
    
    const signatureDateInput = document.getElementById('signatureDate');
    if (signatureDateInput) {
        signatureDateInput.addEventListener('input', function() {
            formatDateInput(this);
        });
        
        // Set today's date as default
        const today = new Date();
        signatureDateInput.value = formatAustralianDate(today.toISOString().split('T')[0]);
    }

    // Format ABN input
    const abnInput = document.getElementById('abn');
    if (abnInput) {
        abnInput.addEventListener('input', function() {
            formatABN(this);
        });
    }

    // Format Head Contractor ABN input
    const clientAbnInput = document.getElementById('clientAbn');
    if (clientAbnInput) {
        clientAbnInput.addEventListener('input', function() {
            formatABN(this);
        });
    }

    // Format phone input
    const phoneInput = document.getElementById('mobile');
    if (phoneInput) {
        phoneInput.addEventListener('input', function() {
            formatPhone(this);
        });
    }

    // Clear signature button
    const clearBtn = document.getElementById('clearSignature');
    if (clearBtn) {
        clearBtn.addEventListener('click', clearSignature);
    }
    
    // Initial form summary update
    updateFormSummary();
    
    // Initial declaration display update
    updateDeclarationDisplay();
    
    // Initialize MapBox Address Autocomplete
    initializeMapBoxAddressAutocomplete();
    
    console.log('Subcontractors Declaration form initialized successfully with enhanced submission handling');
});

// Expose functions globally for HTML access
window.updateTermsAgreement = updateTermsAgreement;
window.clearSignature = clearSignature;
window.updateDeclarationDisplay = updateDeclarationDisplay;
window.toggleOtherPositionField = toggleOtherPositionField;

// Clean up on page unload
window.addEventListener('beforeunload', function() {
    if (formSubmissionManager) {
        formSubmissionManager.destroy();
    }
});

// =============================================================================
// MapBox Address Autocomplete Integration
// =============================================================================

class AddressAutocomplete {
    constructor(inputElement, apiKey) {
        this.input = inputElement;
        this.apiKey = apiKey;
        this.suggestions = [];
        this.selectedIndex = -1;
        this.searchTimeout = null;
        this.isSelecting = false;
        this.init();
    }

    init() {
        // Create suggestion dropdown
        this.dropdown = document.getElementById('addressSuggestions');
        if (!this.dropdown) {
            console.error('Address suggestions dropdown not found');
            return;
        }

        // Event listeners
        this.input.addEventListener('input', this.handleInput.bind(this));
        this.input.addEventListener('focus', this.handleFocus.bind(this));
        this.input.addEventListener('keydown', this.handleKeydown.bind(this));
        
        // Close dropdown when clicking outside
        document.addEventListener('click', (e) => {
            if (!this.input.contains(e.target) && !this.dropdown.contains(e.target)) {
                this.hideSuggestions();
            }
        });
    }

    handleInput(event) {
        if (this.isSelecting) return;

        const query = event.target.value.trim();

        // Clear previous timeout
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }
        
        if (query.length < 3) {
            this.hideSuggestions();
            return;
        }
        
        // Debounce the search
        this.searchTimeout = setTimeout(() => {
            this.searchAddresses(query);
        }, 300);
    }

    handleFocus() {
        if (this.suggestions.length > 0) {
            this.showSuggestions();
        }
    }

    handleKeydown(event) {
        if (!this.dropdown.classList.contains('hidden')) {
            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    this.navigateSuggestions(1);
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    this.navigateSuggestions(-1);
                    break;
                case 'Enter':
                    event.preventDefault();
                    if (this.selectedIndex >= 0 && this.suggestions[this.selectedIndex]) {
                        this.selectAddress(this.suggestions[this.selectedIndex]);
                    }
                    break;
                case 'Escape':
                    this.hideSuggestions();
                    break;
            }
        }
    }

    async searchAddresses(query) {
        try {
            this.showLoading();
            
            const response = await fetch(
                `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?` +
                `access_token=${this.apiKey}&country=AU&types=address&limit=5&autocomplete=true`
            );
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            this.suggestions = data.features.map(feature => ({
                address: feature.place_name,
                coordinates: feature.center,
                components: this.parseAddressComponents(feature)
            }));
            
            this.renderSuggestions();
        } catch (error) {
            console.error('Address search error:', error);
            this.showError();
        }
    }

    parseAddressComponents(feature) {
        const components = {
            street: '',
            suburb: '',
            state: '',
            postcode: '',
            country: ''
        };
        
        // Extract components from the feature
        if (feature.context) {
            feature.context.forEach(ctx => {
                if (ctx.id.includes('postcode')) {
                    components.postcode = ctx.text;
                } else if (ctx.id.includes('place')) {
                    components.suburb = ctx.text;
                } else if (ctx.id.includes('region')) {
                    components.state = ctx.text;
                } else if (ctx.id.includes('country')) {
                    components.country = ctx.text;
                }
            });
        }
        
        // Extract street from place_name
        const addressParts = feature.place_name.split(',');
        if (addressParts.length > 0) {
            components.street = addressParts[0].trim();
        }
        
        return components;
    }

    navigateSuggestions(direction) {
        const maxIndex = this.suggestions.length - 1;
        
        if (direction > 0) {
            this.selectedIndex = Math.min(this.selectedIndex + 1, maxIndex);
        } else {
            this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
        }
        
        this.highlightSelectedSuggestion();
    }

    highlightSelectedSuggestion() {
        const items = this.dropdown.querySelectorAll('.suggestion-item');
        items.forEach((item, index) => {
            if (index === this.selectedIndex) {
                item.classList.add('bg-gold-50');
                item.scrollIntoView({ block: 'nearest' });
            } else {
                item.classList.remove('bg-gold-50');
            }
        });
    }

    showLoading() {
        this.dropdown.innerHTML = '<div class="loading-suggestion"><i class="fas fa-spinner fa-spin mr-2"></i>Searching addresses...</div>';
        this.showSuggestions();
    }

    showError() {
        this.dropdown.innerHTML = '<div class="no-results"><i class="fas fa-exclamation-triangle mr-2"></i>Error searching addresses. Please try again.</div>';
        this.showSuggestions();
    }

    renderSuggestions() {
        this.dropdown.innerHTML = '';
        this.selectedIndex = -1;
        
        if (this.suggestions.length === 0) {
            this.dropdown.innerHTML = '<div class="no-results">No addresses found. Please try a different search.</div>';
            this.showSuggestions();
            return;
        }
        
        this.suggestions.forEach((suggestion, index) => {
            const item = document.createElement('div');
            item.className = 'suggestion-item';
            
            // Create formatted display
            const addressParts = suggestion.address.split(',');
            const mainAddress = addressParts[0];
            const details = addressParts.slice(1).join(',').trim();
            
            item.innerHTML = `
                <div class="suggestion-address">${mainAddress}</div>
                ${details ? `<div class="suggestion-details">${details}</div>` : ''}
            `;
            
            item.addEventListener('click', () => {
                this.selectAddress(suggestion);
            });
            
            item.addEventListener('mouseenter', () => {
                this.selectedIndex = index;
                this.highlightSelectedSuggestion();
            });
            
            this.dropdown.appendChild(item);
        });
        
        this.showSuggestions();
    }

    selectAddress(suggestion) {
        this.isSelecting = true;
        this.input.value = suggestion.address;
        this.hideSuggestions();

        // Trigger change event for form validation and summary update
        this.input.dispatchEvent(new Event('change', { bubbles: true }));

        this.isSelecting = false;
        console.log('Selected address:', suggestion);
    }

    showSuggestions() {
        if (this.suggestions.length > 0 || this.dropdown.innerHTML.includes('loading-suggestion') || this.dropdown.innerHTML.includes('no-results')) {
            this.dropdown.classList.remove('hidden');
        }
    }

    hideSuggestions() {
        this.dropdown.classList.add('hidden');
        this.selectedIndex = -1;
    }
}

// Initialize MapBox Address Autocomplete
function initializeMapBoxAddressAutocomplete() {
    const addressInput = document.getElementById('fullAddress');
    if (addressInput) {
        // MapBox API Key
        const MAPBOX_API_KEY = 'pk.eyJ1Ijoiam9obmRxdWFsaWZ5bWUiLCJhIjoiY21nc284NHhkMzJwYjJrcTQ3bWV6cjI2eCJ9.4PYfo4Fqx8dB7wwAu-_ZBA';
        
        if (MAPBOX_API_KEY && MAPBOX_API_KEY !== 'YOUR_MAPBOX_API_KEY_HERE') {
            const autocomplete = new AddressAutocomplete(addressInput, MAPBOX_API_KEY);
            console.log('MapBox address autocomplete initialized for Subcontractors Declaration');
        } else {
            console.warn('MapBox API key not configured. Address autocomplete disabled.');
        }
    } else {
        console.log('Registered address input not found');
    }
}
